import { useState, useMemo } from "react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Maximize2, X } from "lucide-react";
import { gallery, images } from "@/mock/mock";

export default function Gallery() {
  const categories = useMemo(() => ["All", ...Array.from(new Set(gallery.map((g) => g.category)))], []);
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = active === "All" ? gallery : gallery.filter((g) => g.category === active);

  return (
    <>
      <PageHeader
        eyebrow="Gallery"
        title="Inside our operations"
        subtitle="A look at our Security Operations Center, forensic lab, infrastructure and the team keeping Canadian organizations secure."
      />

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                  active === c
                    ? "bg-brand-lime text-brand-navy"
                    : "border border-white/10 bg-white/5 text-slate-300 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((g, i) => (
              <Reveal key={g.key} delay={(i % 3) * 70}>
                <button
                  onClick={() => setLightbox(g)}
                  className="group relative block w-full overflow-hidden rounded-2xl border border-white/10 text-left"
                >
                  <img src={images[g.key]} alt={g.title} className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/20 to-transparent opacity-80" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="grid h-12 w-12 place-items-center rounded-full bg-brand-lime text-brand-navy"><Maximize2 className="h-5 w-5" /></span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium text-brand-lime backdrop-blur">{g.category}</span>
                    <p className="mt-2 font-display font-semibold text-white">{g.title}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="bg-brand-navy2 border-white/10 text-white max-w-4xl p-2">
          {lightbox && (
            <div>
              <img src={images[lightbox.key]} alt={lightbox.title} className="w-full max-h-[75vh] object-contain rounded-lg" />
              <div className="flex items-center justify-between px-3 py-3">
                <div>
                  <p className="font-display font-semibold text-white">{lightbox.title}</p>
                  <p className="text-sm text-brand-lime">{lightbox.category}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
